import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Capacitor } from '@capacitor/core'
import { KeepAwake } from '@capacitor-community/keep-awake'
import { useLogger } from './useLogger'

export function useWakeLock() {
  const logger = useLogger()
  let wakeLock = null

  // Function to acquire wake lock
  const acquireWakeLock = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // For Capacitor native apps
        logger.debug('WakeLock: Acquiring wake lock for native app')
        await KeepAwake.keepAwake()
        logger.debug('WakeLock: Successfully acquired wake lock for native app')
      } else {
        // For PWA and browser
        if ('wakeLock' in navigator) {
          logger.debug('WakeLock: Acquiring wake lock for PWA/browser')
          wakeLock = await navigator.wakeLock.request('screen')
          logger.debug('WakeLock: Successfully acquired wake lock for PWA/browser')
        } else {
          logger.debug('WakeLock: Wake Lock API not supported in this browser')
        }
      }
    } catch (err) {
      logger.error('WakeLock: Failed to acquire wake lock:', err)
    }
  }

  // Function to release wake lock
  const releaseWakeLock = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // For Capacitor native apps
        logger.debug('WakeLock: Releasing wake lock for native app')
        await KeepAwake.allowSleep()
        logger.debug('WakeLock: Successfully released wake lock for native app')
      } else {
        // For PWA and browser
        if (wakeLock) {
          logger.debug('WakeLock: Releasing wake lock for PWA/browser')
          await wakeLock.release()
          wakeLock = null
          logger.debug('WakeLock: Successfully released wake lock for PWA/browser')
        }
      }
    } catch (err) {
      logger.error('WakeLock: Failed to release wake lock:', err)
    }
  }

  // Handle visibility change for PWA
  const handleVisibilityChange = async () => {
    logger.debug('WakeLock: Visibility changed:', document.visibilityState)
    if (document.visibilityState === 'visible') {
      await acquireWakeLock()
    } else {
      await releaseWakeLock()
    }
  }

  // Setup on mount
  onMounted(async () => {
    logger.debug('WakeLock: Component mounted, platform:', Capacitor.isNativePlatform() ? 'Native' : 'Web')
    await acquireWakeLock()
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  // Cleanup on unmount
  onBeforeUnmount(() => {
    logger.debug('WakeLock: Component unmounting')
    releaseWakeLock()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    acquireWakeLock,
    releaseWakeLock
  }
} 